/**
 * BrandImage组件 - 品牌图片展示组件
 * 基于原Vue组件迁移，支持PC端和移动端响应式布局
 * PC端：描述文本在图片右侧居中
 * 移动端：描述文本在图片下方
 */

'use client';

import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useValidExposure } from '@/hooks/useValidExposure';

// 导入统一的类型定义
import { BrandImageData, BrandImageProps, BrandImageTrackingParams } from './types';
import { useMergedRefs } from '@/hooks/useMergedRefs';

// Alt文本生成工具
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  const baseAlt = indexData?.des || componentName;
  return `${baseAlt} - Item ${index + 1}`;
};

// 获取埋点参数
const useTrackingParams = (
  indexData: BrandImageData,
  moduleIndex: number,
): BrandImageTrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id),
      mkt_index: 0,
      mkt_index_sub: '',
      mkt_jump_type: `${indexData.jump_type || ''}`,
      mkt_jump_content: `${indexData.Jump_target || ''}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${indexData.sku?.shop_sku || ''}`,
      business_sku: `${indexData.sku?.business_sku || ''}`,
      product_sku: `${indexData.sku?.sku || ''}`,
      product_id: `${indexData.sku?.id || ''}`,
      product_name: `${indexData.sku?.short_name || ''}`,
      group_id: `${indexData.product_group?.id || ''}`,
      group_name: `${indexData.product_group?.name || ''}`,
    }),
    [indexData, moduleIndex],
  );
};

// 埋点逻辑Hook
const useTrackingHandlers = (
  trackingParams: BrandImageTrackingParams,
  onTrack?: (params: BrandImageTrackingParams) => void,
  onExpTrack?: (params: BrandImageTrackingParams) => void,
  onValidExpTrack?: (params: BrandImageTrackingParams) => void,
) => {
  // 曝光埋点
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('Brand image exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  // 处理点击埋点
  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('Brand image click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

/**
 * BrandImage主组件 - 优化后的双端融合版本
 */
export default function BrandImage({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BrandImageProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取埋点参数
  const trackingParams = useTrackingParams(indexData, moduleIndex);

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  const brandImageRef = useMergedRefs(expRef, validExposureRef);
  // 统一的图片配置
  const imageConfig = useMemo(
    () => ({
      width: isPC ? 1920 : 375,
      height: isPC ? 1080 : 184,
      loading: moduleIndex < (isPC ? 3 : 4) ? ('eager' as const) : ('lazy' as const),
      priority: moduleIndex < (isPC ? 3 : 4),
    }),
    [isPC, moduleIndex],
  );

  // 统一的样式配置
  const styleConfig = useMemo(
    () => ({
      container: isPC ? 'relative w-full' : 'relative w-full min-h-[184px] overflow-hidden',
      image: isPC
        ? 'w-full h-auto object-cover block'
        : 'w-full h-auto object-cover block absolute z-[9]',
      description: isPC
        ? 'absolute top-1/2 right-[48px] transform -translate-y-1/2 w-[47.0833333333vw] text-[14px] leading-[22px] font-ir'
        : 'relative z-10 w-[347px] pt-[198px] pb-[40px] mx-auto text-[12px] leading-[20px] font-ir',
      defaultTextColor: isPC ? '#ffffff' : '#000000',
    }),
    [isPC],
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: indexData.jump_type || 0,
      Jump_target: indexData.Jump_target || '',
      ...(indexData.sku?.shop_sku && { sku: { shop_sku: indexData.sku.shop_sku } }),
      ...(indexData.product_group?.id && {
        product_group: { id: String(indexData.product_group.id) },
      }),
    }),
    [indexData],
  );

  // 如果没有图片，不渲染组件
  if (!indexData?.jump_image) {
    return null;
  }

  return (
    <div ref={brandImageRef} className="brand-image-container relative">
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={getAlt('BrandImage', 0, indexData)}
        className="block w-full"
      >
        {/* 统一的容器结构 */}
        <div className={styleConfig.container}>
          {/* 统一的图片组件 */}
          <Image
            src={indexData.jump_image}
            alt={getAlt('BrandImage', 0, indexData)}
            width={imageConfig.width}
            height={imageConfig.height}
            loading={imageConfig.loading}
            className={styleConfig.image}
            style={{
              aspectRatio: indexData.jump_img_ratio || 'auto',
            }}
            priority={imageConfig.priority}
          />

          {/* 统一的描述文本组件 */}
          {indexData.des && (
            <div
              className={styleConfig.description}
              style={{
                color: indexData.text_color || styleConfig.defaultTextColor,
              }}
              dangerouslySetInnerHTML={{
                __html: indexData.des,
              }}
            />
          )}
        </div>
      </MagicJump>
    </div>
  );
}
